import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

/**
 * Admin-only view of every post-close edit logged by utils/fy_lock.py.
 * Filterable by FY, record kind, record id, or user.
 */
const ACTION_TONE = {
  create: 'bg-blue-100 text-blue-800',
  update: 'bg-amber-100 text-amber-800',
  delete: 'bg-rose-100 text-rose-800',
};

const FYAuditLog = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    fy_id: '',
    record_kind: '',
    record_id: '',
    user_id: '',
  });
  const [fyOptions, setFyOptions] = useState([]);

  const isAdmin = user && ['admin', 'super_admin'].includes(user.role);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v) params[k] = v;
      });
      const res = await api.get('/fy-audit-log', { params });
      setItems(res.data?.items || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load audit log.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const res = await api.get('/fiscal-years');
        setFyOptions(res.data?.items || []);
      } catch {
        /* ignore */
      }
    })();
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  if (!isAdmin) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded p-4">
          Admin access required.
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6 max-w-6xl mx-auto" data-testid="fy-audit-log-page">
      <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">📋 FY Audit Log</h1>
      <p className="text-xs sm:text-sm text-gray-600 mb-4">
        Every post-close edit to a record dated inside a closed fiscal year is logged here.
        Soft-lock policy: non-admins are blocked outright; admin edits go through but are recorded for auditability.
      </p>

      {/* Filters */}
      <div className="bg-white border rounded-lg p-3 mb-4 grid grid-cols-1 sm:grid-cols-5 gap-2">
        <select
          value={filters.fy_id}
          onChange={(e) => setFilters((p) => ({ ...p, fy_id: e.target.value }))}
          className="border rounded px-2 py-1 text-sm"
          data-testid="fy-audit-filter-fy"
        >
          <option value="">All FYs</option>
          {fyOptions.map((fy) => (
            <option key={fy.id} value={fy.id}>
              {fy.name}
            </option>
          ))}
        </select>
        <select
          value={filters.record_kind}
          onChange={(e) => setFilters((p) => ({ ...p, record_kind: e.target.value }))}
          className="border rounded px-2 py-1 text-sm"
          data-testid="fy-audit-filter-kind"
        >
          <option value="">All record kinds</option>
          <option value="sales_invoice">Sales invoice</option>
          <option value="payment">Payment</option>
          <option value="purchase_entry">Purchase entry</option>
          <option value="other_purchase">Other purchase</option>
        </select>
        <input
          type="text"
          placeholder="Record id / invoice #"
          value={filters.record_id}
          onChange={(e) => setFilters((p) => ({ ...p, record_id: e.target.value }))}
          className="border rounded px-2 py-1 text-sm"
          data-testid="fy-audit-filter-record"
        />
        <input
          type="text"
          placeholder="User id"
          value={filters.user_id}
          onChange={(e) => setFilters((p) => ({ ...p, user_id: e.target.value }))}
          className="border rounded px-2 py-1 text-sm"
          data-testid="fy-audit-filter-user"
        />
        <button
          type="button"
          onClick={fetchData}
          className="px-3 py-1.5 rounded bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
          data-testid="fy-audit-apply-btn"
        >
          Apply
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded p-3 mb-3" data-testid="fy-audit-error">
          {error}
        </div>
      )}

      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="px-4 py-2 border-b bg-gray-50 text-xs text-gray-600 flex justify-between">
          <span>{loading ? 'Loading…' : `${items.length} log entries`}</span>
        </div>
        {!loading && items.length === 0 ? (
          <div className="p-6 text-sm text-gray-500 text-center">
            No post-close edits recorded yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="fy-audit-table">
              <thead className="bg-gray-50 text-gray-500 uppercase">
                <tr>
                  <th className="text-left px-2 py-2 whitespace-nowrap">Timestamp</th>
                  <th className="text-left px-2 py-2">User</th>
                  <th className="text-left px-2 py-2">FY</th>
                  <th className="text-left px-2 py-2">Record</th>
                  <th className="text-left px-2 py-2">Date</th>
                  <th className="text-left px-2 py-2">Action</th>
                  <th className="text-left px-2 py-2">Before → After</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.id} className="border-t align-top">
                    <td className="px-2 py-2 whitespace-nowrap text-gray-700">
                      {row.timestamp ? new Date(row.timestamp).toLocaleString() : '—'}
                    </td>
                    <td className="px-2 py-2">
                      <div className="font-semibold">{row.user_name || row.user_email}</div>
                      <div className="text-[10px] text-gray-500">{row.user_role}</div>
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap">{row.fy_name}</td>
                    <td className="px-2 py-2">
                      <div className="font-mono">{row.record_id}</div>
                      <div className="text-[10px] text-gray-500">{row.record_kind}</div>
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap">{row.record_date}</td>
                    <td className="px-2 py-2">
                      <span
                        className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${
                          ACTION_TONE[row.action] || 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {row.action}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-[11px] font-mono leading-tight">
                      {row.before && (
                        <pre className="whitespace-pre-wrap bg-rose-50 border border-rose-100 rounded px-1 py-0.5 mb-1">
                          {JSON.stringify(row.before, null, 0)}
                        </pre>
                      )}
                      {row.after && (
                        <pre className="whitespace-pre-wrap bg-emerald-50 border border-emerald-100 rounded px-1 py-0.5">
                          {JSON.stringify(row.after, null, 0)}
                        </pre>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default FYAuditLog;
